// Seller Service
// Business logic for seller/user operations

import {
    getUserDocument,
    setUserDocument,
    updateUserDocument,
    findUserByEmail
} from './firestore-service.js';

/**
 * Get seller information by UID
 */
export async function getSellerInfo(uid) {
    return await getUserDocument(uid);
}

/**
 * Update seller name (first time setup)
 */
export async function updateSellerName(uid, name, email) {
    await setUserDocument(uid, {
        name: name,
        email: email
    }, true);
}

/**
 * Start seller shift
 */
export async function startShift(uid) {
    await updateUserDocument(uid, {
        isOnline: true,
        isPaused: false
    });
}

/**
 * End seller shift
 */
export async function endShift(uid) {
    await updateUserDocument(uid, {
        isOnline: false,
        isPaused: false
    });
}

/**
 * Toggle seller pause status
 */
export async function togglePause(uid, isPaused) {
    await updateUserDocument(uid, {
        isPaused: isPaused
    });
}

/**
 * Get seller name by email
 * Uses cache first, then queries Firestore if not found
 */
export async function getSellerNameByEmail(email, sellersCache = {}) {
    // Try cache first
    const sellerFromCache = Object.values(sellersCache).find(
        seller => seller.email === email
    );

    if (sellerFromCache && sellerFromCache.name) {
        return sellerFromCache.name;
    }

    // Query Firestore as fallback
    const seller = await findUserByEmail(email);

    if (seller && seller.name) {
        return seller.name;
    }

    // Final fallback: return email prefix
    return email.split('@')[0];
}

/**
 * Get active sellers count (online and not paused)
 */
export function getActiveSellersCount(sellersData) {
    return Object.values(sellersData).filter(
        seller => seller.isOnline && !seller.isPaused
    ).length;
}

/**
 * Check if seller is currently busy
 * (has a customer in 'calling' or 'serving' status)
 */
export function isSellerBusy(sellerEmail, allCustomers) {
    return allCustomers.some(customer =>
        (customer.status === 'calling' && customer.calledBy === sellerEmail) ||
        (customer.status === 'serving' && customer.attendedBy === sellerEmail)
    );
}

/**
 * Check if seller can perform actions
 * (shift active and not paused)
 */
export function canSellerPerformActions(isShiftActive, isSellerPaused) {
    return isShiftActive && !isSellerPaused;
}
